import React, { useState } from "react";
import { motion } from "framer-motion";
import logo from "../assets/SimpleCodeLogo.png";
import caseData from "../i18n/casestudies.json";
import delfabroImg from "../assets/retail.jpg";
import retailImg from "../assets/retail.jpg";

// ============================================
// 🔹 DETAIL COMPONENTS
// ============================================

function MetricCard({ value, desc, delay }) {
  const [displayValue, setDisplayValue] = React.useState("");
  const [hasAnimated, setHasAnimated] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!ref.current || hasAnimated) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);

          const match = value.match(/(\d+)/);
          if (!match) {
            setDisplayValue(value);
            return;
          }

          const target = parseInt(match[1]);
          const prefix = value.slice(0, value.indexOf(match[1]));
          const suffix = value.slice(value.indexOf(match[1]) + match[1].length);
          const duration = 1500;
          const steps = 40;
          const stepTime = duration / steps;
          let current = 0;

          const timer = setInterval(() => {
            current += Math.ceil(target / steps);
            if (current >= target) {
              current = target;
              clearInterval(timer);
            }
            setDisplayValue(`${prefix}${current}${suffix}`);
          }, stepTime);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, hasAnimated]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className="rounded-2xl bg-white border border-sky-100 p-6 text-center hover:shadow-md hover:border-sky-200 transition"
    >
      <div className="text-3xl md:text-4xl font-extrabold text-sky-600 mb-1">{displayValue || value}</div>
      <p className="text-sm text-slate-500">{desc}</p>
    </motion.div>
  );
}

function FeatureCard({ title, desc, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      className="rounded-2xl border border-sky-100 bg-white p-6 hover:shadow-md hover:border-sky-200 transition"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0" />
        <h4 className="font-semibold text-slate-900">{title}</h4>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
    </motion.div>
  );
}

// ============================================
// 🔹 INSIGHT BLOCK
// ============================================

function InsightBlock({ insight }) {
  if (!insight) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="max-w-3xl mx-auto"
    >
      <h3 className="text-sm font-semibold uppercase tracking-widest text-amber-500 mb-3">
        {insight.label}
      </h3>
      <div className="rounded-2xl bg-gradient-to-br from-sky-900 via-sky-800 to-sky-900 text-white p-8 md:p-10 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-sky-400/10 rounded-full blur-3xl" />
        <div className="relative">
          <h4 className="text-xl md:text-2xl font-bold mb-4 leading-snug">
            {insight.title}
          </h4>
          <p className="text-sky-100 leading-relaxed text-sm md:text-base">
            {insight.text}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 🔹 DEMO CTA BLOCK
// ============================================

function DemoCTA({ demo }) {
  if (!demo) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="max-w-3xl mx-auto"
    >
      <div className="rounded-2xl border-2 border-sky-200 bg-gradient-to-br from-sky-50 to-white p-8 md:p-10 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-sky-400/10 rounded-full blur-3xl" />

        <div className="relative">
          <h4 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">
            {demo.title}
          </h4>

          <div className="mb-6 rounded-xl overflow-hidden border border-sky-200 shadow-lg mx-auto max-w-lg">
            <div className="bg-slate-900 px-4 py-2 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              <span className="ml-3 text-xs text-slate-400 font-mono">sp-poc1.pages.dev</span>
            </div>
            <div className="bg-gradient-to-br from-slate-900 via-sky-950 to-slate-900 p-6 flex items-center justify-center gap-4">
              <img src={logo} alt="SimplePilot" className="w-10 h-10 opacity-80" />
              <div className="text-left">
                <div className="text-white font-bold text-sm">SimplePilot</div>
                <div className="text-sky-400 text-xs font-semibold uppercase tracking-wider">Retail Intelligence</div>
              </div>
            </div>
          </div>

          <a
            href={demo.url}
            target="_blank"
            rel="noreferrer"
            className="inline-block rounded-2xl bg-sky-600 hover:bg-sky-700 text-white text-lg px-8 py-3.5 font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sky-600/30"
          >
            {demo.buttonText}
          </a>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 🔹 HERO STATS
// ============================================

function HeroStats({ stats }) {
  if (!stats) return null;
  return (
    <div className="mt-8 flex flex-wrap items-center justify-center gap-8">
      {stats.map((s, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
          className="text-center"
        >
          <div className="text-2xl md:text-3xl font-extrabold text-white">{s.value}</div>
          <div className="text-xs text-sky-300 uppercase tracking-wider mt-1">{s.label}</div>
        </motion.div>
      ))}
    </div>
  );
}

// ============================================
// 🔹 DETAIL VIEW
// ============================================

function CaseDetail({ caseItem, onBack, backLabel }) {
  const img = caseItem.id === "delfabro" ? delfabroImg : null;
  const isRetail = caseItem.id === "retail-intelligence";

  return (
    <>
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-900 via-sky-800 to-sky-900 text-white py-16 text-center">
        <motion.div
          animate={{ y: [0, 20, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-10 left-10 w-64 h-64 bg-sky-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [0, -20, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-10 right-10 w-80 h-80 bg-sky-400/20 rounded-full blur-3xl"
        />

        {isRetail && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(56,189,248,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.03) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        )}

        <div className="relative max-w-3xl mx-auto px-4">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-sky-300 bg-sky-500/10 border border-sky-400/20 rounded-full px-4 py-1.5 mb-4">
            {caseItem.tag}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight">
            {caseItem.title.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < caseItem.title.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p className="mt-4 text-lg text-sky-200">{caseItem.summary}</p>

          {!isRetail && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-sky-300">
              <span className="font-semibold text-white">{caseItem.client}</span>
              <span>·</span>
              <a href="https://delfabrohidraulicos.com/" target="_blank" rel="noreferrer" className="text-sky-300 hover:text-white transition underline underline-offset-2">{caseItem.industry}</a>
            </div>
          )}

          {img && (
            <div className="mt-8 flex items-center justify-center gap-5">
              <div className="w-28 h-28 rounded-2xl overflow-hidden border border-sky-400/20 shadow-md bg-white p-3">
                <img src={img} alt={caseItem.client} className="w-full h-full object-contain" />
              </div>
              <span className="text-3xl font-light text-sky-400">+</span>
              <div className="w-28 h-28 rounded-2xl overflow-hidden border border-sky-400/20 shadow-md bg-white p-4 flex items-center justify-center">
                <img src={logo} alt="SimplePilot" className="w-full h-full object-contain" />
              </div>
            </div>
          )}

          {isRetail && (
            <>
              <div className="mt-8 flex items-center justify-center gap-4">
                <motion.img
                  src={logo}
                  alt="SimplePilot"
                  className="w-16 h-16"
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  style={{ filter: "drop-shadow(0 0 20px rgba(56, 189, 248, 0.4))" }}
                />
              </div>
              <HeroStats stats={caseItem.heroStats} />
            </>
          )}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-sky-50 via-white to-sky-50 overflow-x-hidden">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">

            {/* Challenge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="max-w-3xl mx-auto"
            >
              <h3 className="text-sm font-semibold uppercase tracking-widest text-red-400 mb-3">
                {caseItem.challenge.label}
              </h3>
              <div className="rounded-2xl bg-red-50/40 border border-red-100 p-6 md:p-8">
                <p className="text-slate-600 leading-relaxed">{caseItem.challenge.text}</p>
              </div>
            </motion.div>

            {/* Solution */}
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <img src={logo} alt="SimplePilot" className="h-5 w-auto opacity-60" />
                  <h3 className="text-sm font-semibold uppercase tracking-widest text-sky-500">
                    {caseItem.solution.label}
                  </h3>
                </div>
                <div className="rounded-2xl bg-sky-50/40 border border-sky-100 p-6 md:p-8 mb-8">
                  <p className="text-slate-600 leading-relaxed">{caseItem.solution.text}</p>
                </div>
              </motion.div>

              <div className="grid gap-4 md:grid-cols-2">
                {caseItem.solution.features.map((f, i) => (
                  <FeatureCard key={i} title={f.title} desc={f.desc} delay={i * 0.1} />
                ))}
              </div>
            </div>

            {/* Insight Block */}
            {caseItem.insight && <InsightBlock insight={caseItem.insight} />}

            {/* Results */}
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-widest text-sky-600 mb-6 text-center">
                {caseItem.results.label}
              </h3>
              <div className={`grid gap-4 ${caseItem.results.metrics.length === 3 ? 'md:grid-cols-3 max-w-3xl mx-auto' : 'md:grid-cols-4'}`}>
                {caseItem.results.metrics.map((m, i) => (
                  <MetricCard key={i} value={m.value} desc={m.desc} delay={i * 0.1} />
                ))}
              </div>
            </div>

            {/* Demo CTA */}
            {caseItem.demo && <DemoCTA demo={caseItem.demo} />}

            {/* Tech stack */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest mr-2">
                {caseItem.tech.label}
              </span>
              {caseItem.tech.items.map((item, i) => (
                <span
                  key={i}
                  className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Back button */}
          <div className="mt-20 text-center">
            <button
              onClick={onBack}
              className="text-sm font-medium text-sky-600 hover:text-sky-800 transition"
            >
              ← {backLabel}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

// ============================================
// 🔹 CARD (for listing page)
// ============================================

function CaseCard({ caseItem, onClick, delay }) {
  const img = caseItem.id === "delfabro" ? delfabroImg : caseItem.id === "retail-intelligence" ? retailImg : null;
  const isRetail = caseItem.id === "retail-intelligence";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      onClick={onClick}
      className="rounded-2xl border border-sky-100 bg-white overflow-hidden cursor-pointer transition-all hover:border-sky-200 hover:shadow-lg hover:-translate-y-1 group"
    >
      {img && (
        <div className="h-48 overflow-hidden relative">
          <img src={img} alt={caseItem.client} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {isRetail && (
            <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 via-sky-900/30 to-transparent flex items-end p-5">
              <div className="flex items-center gap-5">
                {(caseItem.heroStats || []).slice(0, 3).map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="text-lg font-extrabold text-white">{s.value}</div>
                    <div className="text-[9px] text-sky-200 uppercase tracking-wider">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-6 md:p-8">
        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-sky-600 bg-sky-50 border border-sky-100 rounded-full px-3 py-1 mb-4">
          {caseItem.tag}
        </span>
        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-sky-700 transition-colors">
          {caseItem.title.split('\n').join('. ')}
        </h3>
        <p className="text-sm text-slate-500 mb-4 leading-relaxed">{caseItem.summary}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {!isRetail && (
              <>
                <span className="font-semibold text-slate-600">{caseItem.client}</span>
                <span>·</span>
              </>
            )}
            <span>{caseItem.industry}</span>
          </div>
          <span className="text-sm font-medium text-sky-600 group-hover:translate-x-1 transition-transform">
            →
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 🔹 MAIN PAGE
// ============================================

export default function CaseStudiesPage({ language, onNavigate }) {
  const t = caseData[language] || caseData.en;
  const [selectedId, setSelectedId] = useState(null);

  const selectedCase = selectedId
    ? t.cases.find((c) => c.id === selectedId)
    : null;

  const handleBack = () => {
    setSelectedId(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const handleSelect = (id) => {
    setSelectedId(id);
    window.scrollTo({ top: 0, behavior: "auto" });
    window.history.pushState({ caseId: id }, "");
  };

  React.useEffect(() => {
    const onPopState = () => {
      setSelectedId(null);
      window.scrollTo({ top: 0, behavior: "auto" });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  if (selectedCase) {
    return (
      <CaseDetail
        caseItem={selectedCase}
        onBack={handleBack}
        backLabel={t.backLabel}
      />
    );
  }

  return (
    <>
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-900 via-sky-800 to-sky-900 text-white py-16 text-center">
        <motion.div
          animate={{ y: [0, 20, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-10 left-10 w-64 h-64 bg-sky-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [0, -20, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-10 right-10 w-80 h-80 bg-sky-400/20 rounded-full blur-3xl"
        />
        <div className="relative">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold">{t.pageTitle}</h1>
          <p className="mt-3 text-sky-200 text-lg">{t.pageSubtitle}</p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-sky-50 via-white to-sky-50 overflow-x-hidden">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {t.cases.map((c, i) => (
              <CaseCard
                key={c.id}
                caseItem={c}
                onClick={() => handleSelect(c.id)}
                delay={i * 0.1}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

